import React, { useContext, useEffect, useRef, useState } from 'react';
import ExportMessage from 'components/Dashboard/StakeHolders/ExportMessage';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import normalize from 'components/Dashboard/utilities/normalizeString';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import { store } from 'components/Store';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { useLocation, useParams } from 'react-router-dom';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { RoleOptions, RoleSearchOptions } from 'components/Dashboard/Filters/FiltersData/StakeholdersFilters';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import createNotification from 'components/Settings/Utilities/CreateNotification';

const ExportContactsButton = ({ contactListBar, onlyIcon, isLoading }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, selectedCrmContacts } = state;
  const { contentResults } = useContentResults();
  const { subscriptionStatus, code } = activePlan;
  const { selectedIds, selectedAll, selectedAllVisible } = selectedCrmContacts;

  const sourceRef = useRef(null);

  const params = useParams();
  const { groupId } = params;

  const { generalApiCall } = useGeneralApiCall();

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const includeStaff = getUrlParam('includeStaff') === 'true';

  const searchQuery = query.get('search') ? decodeUrlQueryValue(query.get('search')) : '';

  const sortQuery = query.get('sort') ? query.get('sort').split('_') : null;
  const sort = () => {
    return { field: sortQuery[0], order: sortQuery[1] };
  };

  const [exportMessage, setExportMessage] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const hideExportMessage = () => {
    setExportMessage(false);
  };
  const exportMessageContainer = useRef(null);
  useOutsideElement(exportMessageContainer, hideExportMessage);
  const { getSectionFilters } = useGetSectionFilters('InfluenceContacts');
  const sectionFilters = getSectionFilters();
  const { filters } = sectionFilters;

  useEffect(() => {
    const controller = new AbortController();
    sourceRef.current = controller;
    return () => {
      controller.abort('Export function cancelled');
    };
  }, []);

  const isSelectedCrmContacts = selectedIds.length > 0 || selectedAll || selectedAllVisible.length > 0;

  const contactFilter = (id) => {
    return { field: 'id', operator: 'str_eq', value: `${id}` };
  };

  const exportResultsFunction = async () => {
    setIsExportLoading(true);
    try {
      let requestProperties = {};
      const staffersFilters = [...RoleOptions, ...RoleSearchOptions]?.filter((item) =>
        item?.name?.toLowerCase()?.includes('staff')
      );
      const filtersValues = staffersFilters?.map((item) => item?.filterValue ?? item?.name);
      const includeStaffers = includeStaff || filters?.some((item) => filtersValues?.includes(item?.value));

      const noStaffFilter = staffersFilters?.map((item) => {
        return { field: 'categories', value: item?.filterValue ?? item?.name, operator: 'str_not_eq' };
      });

      const filtersToUse = includeStaffers || groupId ? filters : [...noStaffFilter, ...filters];
      if (filtersToUse) {
        requestProperties = { ...requestProperties, filters: filtersToUse };
      }
      if (!!sortQuery) {
        if (groupId) {
          requestProperties = { ...requestProperties, sorts: [sort(), { field: 'id', order: 'desc' }] };
        } else {
          requestProperties = { ...requestProperties, sort: sort() };
        }
      }

      if (isSelectedCrmContacts) {
        if (selectedIds.length > 0) {
          requestProperties = {
            ...requestProperties,
            filters: selectedIds.map((id) => contactFilter(id)),
          };
        } else if (selectedAllVisible.length > 0) {
          requestProperties = {
            ...requestProperties,
            filters: selectedAllVisible.map((id) => contactFilter(id)),
          };
        } else {
          requestProperties = { ...requestProperties, query: searchQuery };
        }
      }
      requestProperties = { ...requestProperties, query: searchQuery };
      if (groupId) {
        requestProperties = {
          ...requestProperties,
          id: groupId,
        };
      }
      const pathname = `/api/crm-contact/export-to-csv${groupId && selectedAll ? '-group' : ''}`;
      const exportResults = await generalApiCall({
        pathname,
        method: 'post',
        requestProperties,
        needsAuthentication: true,
        requestSource: sourceRef.current,
        returnError: true,
        returnCancelled: true,
        notShowErrorMessage: true,
      });

      if (exportResults && exportResults.name === 'CanceledError') {
        return;
      }

      if (exportResults && typeof exportResults === 'object' && 'status' in exportResults) {
        createNotification('danger', 'Unable to export contacts right now. Please try again.');
        return;
      }

      if (typeof exportResults !== 'string' || exportResults.trim().length === 0) {
        return;
      }
      const csvBlob = new Blob(['\uFEFF', exportResults], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      const fileName = () => {
        return 'Contacts';
      };
      link.setAttribute('href', downloadUrl);
      link.setAttribute('download', `${normalize(fileName().trim())}.csv`);
      document.body.appendChild(link); // Required for FF
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export CRM contacts.', error);
      createNotification('danger', 'Unable to export contacts right now. Please try again.');
    } finally {
      setIsExportLoading(false);
    }
  };

  const CrmContacts = contentResults.CrmContacts;
  const isMobile = useIsMobile(768);

  const numberOfResults = () => {
    if (isSelectedCrmContacts && CrmContacts) {
      if (selectedIds.length > 0) {
        return !isMobile ? `${thousandCommas(selectedIds.length)}` : '';
      } else if (selectedAllVisible.length > 0) {
        return !isMobile ? `${thousandCommas(selectedAllVisible.length)}` : '';
      } else {
        return !isMobile ? `${thousandCommas(CrmContacts.totalHits)}` : '';
      }
    }
    if (CrmContacts) {
      return !isMobile && CrmContacts && CrmContacts.totalHits ? `${thousandCommas(CrmContacts.totalHits)}` : '';
    }
  };
  const onClickHandler = () => {
    if (subscriptionStatus === 'Subscribed' || subscriptionStatus === 'OnFreePlan') {
      exportResultsFunction();
    } else {
      setExportMessage(!exportMessage);
    }
  };
  const disabled = !CrmContacts || (CrmContacts && CrmContacts.currentlyProcessing) || !CrmContacts?.totalHits;
  const isExportDisabled = disabled || isExportLoading || !!isLoading;
  return (
    <>
      {contactListBar ? (
        <button
          className={`contacts-list-bar-action-container-btn ${!onlyIcon ? 'action-container-btn-expanded' : ''}`}
          onClick={() => {
            if (!onlyIcon) {
              onClickHandler();
            }
          }}
          disabled={isExportDisabled}
        >
          {isExportLoading ? (
            <i className='fas fa-spinner fa-spin'></i>
          ) : (
            <span className={`icon-download ${isExportDisabled ? 'icon-download-disabled' : ''}`} />
          )}
          {onlyIcon ? '' : 'Export'}
        </button>
      ) : (
        <div className={`ml-2 filter-main-container position-relative`} ref={exportMessageContainer}>
          <button
            onClick={onClickHandler}
            disabled={isExportDisabled}
            className={`sort-button button-export-results ${
              isExportDisabled && !contactListBar ? 'button-export-contacts' : ''
            }`}
          >
            <span className='filter-name'>
              {isExportLoading ? (
                <i className='fas fa-spinner fa-spin mr-2'></i>
              ) : (
                <>
                  Export {numberOfResults()} result{numberOfResults() === 1 ? '' : 's'}
                </>
              )}
            </span>
          </button>
        </div>
      )}
      <ExportMessage code={code} setShowPopup={setExportMessage} showPopUp={exportMessage} contactsPage={true} />
    </>
  );
};

export default ExportContactsButton;
